import { useState } from 'react';

import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import {
  useCreatePartnerWebhookMutation,
  useDeletePartnerWebhookMutation,
  useGetPartnerWebhooksQuery,
  useTestPartnerWebhookMutation,
  useUpdatePartnerWebhookMutation,
} from '../services/webhooks.api';
import type { Webhook, WebhookCreateRequest, WebhookTestResult } from '../types/webhook';

type WebhookFormState = { kind: 'create' } | { kind: 'edit'; webhook: Webhook } | null;

interface UseWebhooksAvailablePanelStateReturn {
  deleteTarget: Webhook | null;
  formError: string | null;
  formOpen: boolean;
  formState: WebhookFormState;
  handleClearDeleteTarget: () => void;
  handleClearTestTarget: () => void;
  handleConfirmDelete: () => Promise<void>;
  handleFormSubmit: (body: WebhookCreateRequest) => Promise<void>;
  handleRefresh: () => void;
  handleResetForm: () => void;
  handleStartCreate: () => void;
  handleStartDelete: (webhook: Webhook) => void;
  handleStartEdit: (webhook: Webhook) => void;
  handleStartTest: (webhook: Webhook) => void;
  handleTestRequest: (webhookId: number) => Promise<WebhookTestResult>;
  isCreating: boolean;
  isDeleting: boolean;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  isPartialList: boolean;
  limits: { PROJECT_LEVEL: number; SUBPROJECT_LEVEL: number } | undefined;
  pagination: { total: number } | undefined;
  testTarget: Webhook | null;
  webhooks: Webhook[];
}

const useWebhooksAvailablePanelState = (projectId: number): UseWebhooksAvailablePanelStateReturn => {
  const [formState, setFormState] = useState<WebhookFormState>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [testTarget, setTestTarget] = useState<Webhook | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Webhook | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useGetPartnerWebhooksQuery(
    {
      projectId,
      page: 1,
      limit: 10_000,
    },
    { skip: !projectId || projectId <= 0 }
  );

  const [createWebhook, { isLoading: isCreating }] = useCreatePartnerWebhookMutation();
  const [updateWebhook, { isLoading: isUpdating }] = useUpdatePartnerWebhookMutation();
  const [deleteWebhook, { isLoading: isDeleting }] = useDeletePartnerWebhookMutation();
  const [runTestMutation] = useTestPartnerWebhookMutation();

  const webhooks = data?.data ?? [];
  const limits = data?.limits;
  const pagination = data?.pagination;
  const isPartialList = Boolean(pagination && pagination.total > webhooks.length);
  const formOpen = formState !== null;

  const handleFormSubmit = async (body: WebhookCreateRequest): Promise<void> => {
    try {
      if (formState?.kind === 'edit') {
        await updateWebhook({
          webhookId: formState.webhook.id,
          projectId,
          ...body,
        }).unwrap();
      } else {
        await createWebhook({ projectId, ...body }).unwrap();
      }
      setFormState(null);
      refetch().catch(() => {});
    } catch (err) {
      setFormError(extractErrorMessage(err, 'No se pudo guardar el webhook.'));
    }
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) {
      return;
    }

    try {
      await deleteWebhook({ webhookId: deleteTarget.id, projectId }).unwrap();
      setDeleteTarget(null);
      refetch().catch(() => {});
    } catch {
      // Error visible via RTK / network; optional toast omitted
    }
  };

  const handleTestRequest = async (webhookId: number): Promise<WebhookTestResult> => {
    return runTestMutation({ webhookId, projectId }).unwrap();
  };

  const handleClearDeleteTarget = (): void => {
    setDeleteTarget(null);
  };

  const handleClearTestTarget = (): void => {
    setTestTarget(null);
  };

  const handleRefresh = (): void => {
    refetch().catch(() => {});
  };

  const handleResetForm = (): void => {
    setFormState(null);
    setFormError(null);
  };

  const handleStartCreate = (): void => {
    setFormError(null);
    setFormState({ kind: 'create' });
  };

  const handleStartDelete = (webhook: Webhook): void => {
    setDeleteTarget(webhook);
  };

  const handleStartEdit = (webhook: Webhook): void => {
    setFormError(null);
    setFormState({ kind: 'edit', webhook });
  };

  const handleStartTest = (webhook: Webhook): void => {
    setTestTarget(webhook);
  };

  return {
    deleteTarget,
    formError,
    formOpen,
    formState,
    handleClearDeleteTarget,
    handleClearTestTarget,
    handleConfirmDelete,
    handleFormSubmit,
    handleRefresh,
    handleResetForm,
    handleStartCreate,
    handleStartDelete,
    handleStartEdit,
    handleStartTest,
    handleTestRequest,
    isCreating,
    isDeleting,
    isError,
    isFetching,
    isLoading,
    isUpdating,
    isPartialList,
    limits,
    pagination,
    testTarget,
    webhooks,
  };
};

export type { WebhookFormState, UseWebhooksAvailablePanelStateReturn };
export default useWebhooksAvailablePanelState;
